<div
  class="unpaid-backdrop"
  [class.unpaid-backdrop--blocking]="blocking"
  (click)="onBackdropClick()"
></div>

<div
  class="unpaid-modal"
  [class.unpaid-modal--suspended]="suspended"
  role="dialog"
  aria-modal="true"
  aria-labelledby="unpaidModalTitle"
>
  <span class="unpaid-modal__accent"></span>

  <button
    *ngIf="!blocking"
    type="button"
    class="unpaid-modal__close"
    aria-label="Close"
    (click)="dismissed.emit()"
  >
    &times;
  </button>

  <div class="unpaid-modal__head">
    <span class="unpaid-modal__icon" aria-hidden="true">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 8v5m0 3.5h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
    <h2 class="unpaid-modal__title" id="unpaidModalTitle">
      {{
        (suspended
          ? TranslationEnum.Invoices.SUSPENDED_MODAL_TITLE
          : TranslationEnum.Invoices.UNPAID_MODAL_TITLE
        ) | translate
      }}
    </h2>
  </div>

  <div class="unpaid-modal__body">
    <p class="unpaid-modal__intro">
      {{
        suspended
          ? (TranslationEnum.Invoices.SUSPENDED_MODAL_INTRO | translate)
          : (TranslationEnum.Invoices.UNPAID_MODAL_INTRO
            | translate: { count: status?.unpaidCount || 0 })
      }}
    </p>

    <!-- Countdown: only while the gym still has time left to pay. -->
    <div class="unpaid-countdown" *ngIf="!suspended && hasGracePeriod">
      <div class="unpaid-countdown__row">
        <span class="unpaid-countdown__days">{{ daysLeft }}</span>
        <span class="unpaid-countdown__label">
          {{ TranslationEnum.Invoices.DAYS_LEFT | translate }}
        </span>
      </div>
      <div
        class="unpaid-countdown__track"
        role="progressbar"
        [attr.aria-valuenow]="graceUsedPercent"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <span
          class="unpaid-countdown__fill"
          [style.width.%]="graceUsedPercent"
        ></span>
      </div>
      <p class="unpaid-countdown__caption">
        {{
          isLastDay
            ? (TranslationEnum.Invoices.UNPAID_MODAL_GRACE_LAST_DAY | translate)
            : (TranslationEnum.Invoices.UNPAID_MODAL_GRACE
              | translate: { days: daysLeft })
        }}
      </p>
    </div>

    <p class="unpaid-modal__intro" *ngIf="!suspended && !hasGracePeriod">
      {{ TranslationEnum.Invoices.UNPAID_MODAL_NO_GRACE | translate }}
    </p>

    <!-- Falls back to the totals when the API has not shipped the list yet. -->
    <div class="unpaid-invoices" *ngIf="listedInvoices.length; else totalsOnly">
      <div class="unpaid-invoices__head">
        <span>{{ TranslationEnum.Invoices.INVOICE_NUMBER | translate }}</span>
        <span>{{ TranslationEnum.Invoices.INVOICE_DUE_DATE | translate }}</span>
        <span class="unpaid-invoices__amount">
          {{ TranslationEnum.Invoices.INVOICE_AMOUNT | translate }}
        </span>
      </div>

      <a
        class="unpaid-invoices__row"
        *ngFor="let invoice of listedInvoices"
        [href]="downloadUrl(invoice)"
        [attr.download]="'invoice-' + invoice.invoiceNumber + '.pdf'"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span class="unpaid-invoices__number">{{ invoice.invoiceNumber }}</span>
        <span class="unpaid-invoices__due">
          {{ invoice.dueDate | date: "dd MMM yyyy" }}
        </span>
        <span class="unpaid-invoices__amount">
          {{ invoice.amount | number: "1.2-2" }} {{ invoice.currency }}
        </span>
      </a>

      <p class="unpaid-invoices__more" *ngIf="hiddenInvoiceCount">
        {{
          TranslationEnum.Invoices.UNPAID_MODAL_MORE_INVOICES
            | translate: { count: hiddenInvoiceCount }
        }}
      </p>

      <div class="unpaid-invoices__total">
        <span>{{
          TranslationEnum.Invoices.UNPAID_MODAL_TOTAL | translate
        }}</span>
        <span
          >{{ status.totalAmount | number: "1.2-2" }}
          {{ status.currency }}</span
        >
      </div>
    </div>

    <ng-template #totalsOnly>
      <dl class="unpaid-facts">
        <div class="unpaid-facts__row" *ngIf="status?.totalAmount">
          <dt>
            {{ TranslationEnum.Invoices.UNPAID_MODAL_OUTSTANDING | translate }}
          </dt>
          <dd class="unpaid-facts__strong">
            {{ status.totalAmount | number: "1.2-2" }} {{ status.currency }}
          </dd>
        </div>
        <div class="unpaid-facts__row" *ngIf="status?.oldestDueDate">
          <dt>
            {{ TranslationEnum.Invoices.UNPAID_MODAL_OLDEST_DUE | translate }}
          </dt>
          <dd>{{ status.oldestDueDate | date: "dd MMM yyyy" }}</dd>
        </div>
      </dl>
    </ng-template>

    <p class="unpaid-modal__suspension" *ngIf="status?.suspensionDate">
      {{
        (suspended
          ? TranslationEnum.Invoices.SUSPENDED_MODAL_SINCE
          : TranslationEnum.Invoices.UNPAID_MODAL_SUSPENSION_DATE
        ) | translate
      }}:
      <strong>{{ status.suspensionDate | date: "dd MMM yyyy" }}</strong>
    </p>

    <p class="unpaid-modal__note">
      {{
        !suspended
          ? (TranslationEnum.Invoices.UNPAID_MODAL_THANKS | translate)
          : canViewInvoices
          ? (TranslationEnum.Invoices.SUSPENDED_MODAL_HELP | translate)
          : (TranslationEnum.Invoices.SUSPENDED_MODAL_STAFF_HELP | translate)
      }}
    </p>
  </div>

  <div class="unpaid-modal__footer">
    <button
      type="button"
      class="btn unpaid-modal__btn unpaid-modal__btn--ghost"
      (click)="blocking ? loggedOut.emit() : dismissed.emit()"
    >
      {{
        (blocking
          ? TranslationEnum.Invoices.SUSPENDED_MODAL_LOGOUT
          : TranslationEnum.Invoices.UNPAID_MODAL_REMIND_LATER
        ) | translate
      }}
    </button>
    <button
      *ngIf="canViewInvoices"
      type="button"
      class="btn unpaid-modal__btn unpaid-modal__btn--primary"
      (click)="viewInvoices.emit()"
    >
      {{ TranslationEnum.Invoices.UNPAID_MODAL_VIEW_INVOICES | translate }}
    </button>
  </div>
</div>
